import { useState } from "react";
import { useTranslation } from "../i18n/useTranslation";

interface OnboardingProps {
  /** Called when the user finishes or skips. */
  onDone: () => void;
}

interface Card {
  key: string;
  glyph: string;
  accent: string;
}

const CARDS: Card[] = [
  { key: "welcome", glyph: "◆", accent: "var(--color-cyan)" },
  { key: "how", glyph: "↑", accent: "var(--color-green)" },
  { key: "expect", glyph: "✓", accent: "var(--color-purple)" },
];

/**
 * First-run onboarding — three skippable cards shown before the menu the
 * first time a tester opens the app. Honest framing on the last card mirrors
 * the marketing site's science section (no broad-IQ overclaims).
 */
export function Onboarding({ onDone }: OnboardingProps) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const card = CARDS[index];
  const isLast = index === CARDS.length - 1;

  return (
    <main className="min-h-screen flex flex-col px-6 py-8 max-w-md mx-auto">
      {/* Skip */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onDone}
          className="font-mono text-[11px] tracking-[0.06em] text-fg-3 hover:text-fg-0 transition-colors"
        >
          {t("onboarding.skip")}
        </button>
      </div>

      {/* Card body */}
      <section className="flex-1 flex flex-col items-center justify-center text-center gap-6">
        <div
          className="w-20 h-20 rounded-2xl flex items-center justify-center text-3xl"
          style={{
            background: `color-mix(in oklab, ${card.accent} 16%, transparent)`,
            color: card.accent,
            border: `1px solid ${card.accent}`,
          }}
          aria-hidden
        >
          {card.glyph}
        </div>

        <h1 className="text-3xl md:text-4xl font-medium tracking-[-0.02em]">
          {t(`onboarding.${card.key}.title`)}
        </h1>

        <p className="text-fg-1 leading-relaxed max-w-sm">
          {t(`onboarding.${card.key}.body`)}
        </p>
      </section>

      {/* Dots + advance */}
      <footer className="flex flex-col items-center gap-6">
        <div className="flex gap-2" aria-hidden>
          {CARDS.map((c, i) => (
            <span
              key={c.key}
              className={[
                "h-1.5 rounded-full transition-all",
                i === index ? "w-6 bg-fg-0" : "w-1.5 bg-bg-3",
              ].join(" ")}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={() => (isLast ? onDone() : setIndex((i) => i + 1))}
          className="w-full max-w-xs px-7 py-3 rounded-full bg-cyan text-bg-1 text-sm font-semibold active:scale-95 transition-transform"
        >
          {isLast ? t("onboarding.start") : t("onboarding.next")}
        </button>
      </footer>
    </main>
  );
}
